/**
 * command-deck.js — the portfolio's command palette.
 *
 * Replaces product search with a terminal: fuzzy-matched routes plus
 * runnable "programs" (arcade, invert, gravity, matrix…). Opens from
 * the header trigger, ⌘K / Ctrl+K, or the `/` key. Effects are
 * delegated to window.PFX (portfolio-fx.js).
 */
(() => {
  const root = document.getElementById('search-modal');
  const dialog = root?.querySelector('dialog');
  const frame = root?.querySelector('[data-deck]');
  if (!root || !dialog || !frame) return;

  const input = frame.querySelector('[data-deck-input]');
  const list = frame.querySelector('[data-deck-list]');
  const empty = frame.querySelector('[data-deck-empty]');
  const count = frame.querySelector('[data-deck-count]');
  const status = frame.querySelector('[data-deck-status]');

  const pfx = () => window.PFX || null;
  const toast = (msg) => (pfx()?.toast ? pfx().toast(msg) : console.info(`[deck] ${msg}`));

  /* ----- Index ----- */
  let email = 'hello@jdenandro.com';
  let routes = [];
  try {
    const data = JSON.parse(document.querySelector('[data-deck-index]')?.textContent || '{}');
    email = data.email || email;
    routes = Array.isArray(data.routes) ? data.routes : [];
  } catch (error) {
    routes = [];
  }

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      toast(`COPIED → ${email}`);
    } catch (error) {
      toast(`EMAIL → ${email}`);
    }
  };

  const PROGRAMS = [
    {
      id: 'play',
      label: 'play',
      hint: 'CHAOS — DENANDRO.EXE, the arcade runner',
      glyph: '▶',
      group: 'RUN',
      keywords: 'game arcade runner fun chaos',
      url: '/pages/play',
    },
    {
      id: 'city',
      label: 'city',
      hint: 'PEACE — walk DENANDROPOLIS at night',
      glyph: '⌂',
      group: 'RUN',
      keywords: 'open world walk explore night npc game denandropolis peace calm',
      url: '/pages/city',
    },
    {
      id: 'instagram',
      label: 'instagram',
      hint: '@jdenandro — life, love & motivation. 12K tuning in.',
      glyph: '◎',
      group: 'RUN',
      keywords: 'ig social follow content reels feed signal life love motivation talks',
      run: () => window.open('https://www.instagram.com/jdenandro', '_blank', 'noopener'),
    },
    {
      id: 'contact',
      label: 'contact',
      hint: 'open a line to the studio',
      glyph: '@',
      group: 'RUN',
      keywords: 'talk hire email form',
      url: '/pages/contact',
    },
    {
      id: 'email',
      label: 'copy email',
      hint: email,
      glyph: '⧉',
      group: 'RUN',
      keywords: 'mail clipboard address',
      run: copyEmail,
    },
    {
      id: 'invert',
      label: 'invert',
      hint: 'flip the world — white ⇄ black',
      glyph: '◐',
      group: 'SYS',
      keywords: 'dark mode theme night flip negative',
      run: () => pfx()?.invert?.(),
    },
    {
      id: 'gravity',
      label: 'gravity',
      hint: 'let the page fall. it recovers.',
      glyph: '⇣',
      group: 'SYS',
      keywords: 'fall physics drop chaos',
      run: () => pfx()?.gravity?.(),
    },
    {
      id: 'matrix',
      label: 'matrix',
      hint: 'eight seconds of rain',
      glyph: '▚',
      group: 'SYS',
      keywords: 'rain code glyphs neo',
      run: () => pfx()?.matrix?.(),
    },
    {
      id: 'smoke',
      label: 'smoke',
      hint: 'toggle the cursor vapor',
      glyph: '~',
      group: 'SYS',
      keywords: 'cursor trail particles off on',
      run: () => pfx()?.toggleSmoke?.(),
    },
    {
      id: 'boring',
      label: 'boring',
      hint: 'kill all magic (persists — run again to undo)',
      glyph: '∅',
      group: 'SYS',
      keywords: 'disable effects reduce motion plain accessibility',
      run: () => pfx()?.toggleBoring?.(),
    },
    {
      id: 'roll',
      label: 'do a barrel roll',
      hint: 'the page obliges',
      glyph: '↻',
      group: 'SYS',
      keywords: 'barrel roll spin rotate 360',
      run: () => pfx()?.roll?.(),
    },
    {
      id: 'flip',
      label: 'flip',
      hint: 'australia mode, four seconds',
      glyph: '↕',
      group: 'SYS',
      keywords: 'upside down rotate 180 australia',
      run: () => pfx()?.flip?.(),
    },
    {
      id: 'zen',
      label: 'zen',
      hint: 'breathe. the backlog can wait.',
      glyph: '○',
      group: 'SYS',
      keywords: 'breathe meditate calm relax pause',
      run: () => pfx()?.zen?.(),
    },
    {
      id: 'stats',
      label: 'stats',
      hint: 'your session, quantified',
      glyph: '∑',
      group: 'SYS',
      keywords: 'session telemetry clicks scroll time',
      run: () => pfx()?.stats?.(),
    },
    {
      id: 'hack',
      label: 'hack',
      hint: 'gain access to absolutely nothing',
      glyph: '⚿',
      group: 'SYS',
      keywords: 'hacker terminal exploit mainframe',
      run: () => pfx()?.hack?.(),
    },
    {
      id: 'bali',
      label: 'bali',
      hint: 'studio local time & vibes',
      glyph: '☾',
      group: 'SYS',
      keywords: 'time timezone gmt8 indonesia surf weather',
      run: () => pfx()?.bali?.(),
    },
    {
      id: 'dvd',
      label: 'dvd',
      hint: 'the screensaver. it never hits the corner.',
      glyph: '▢',
      group: 'SYS',
      keywords: 'screensaver bounce logo idle',
      run: () => pfx()?.dvd?.(),
    },
    {
      id: 'bot',
      label: 'bot',
      hint: 'summon the runner. it is busy.',
      glyph: '⚉',
      group: 'SYS',
      keywords: 'runner mascot cameo sprite',
      run: () => pfx()?.bot?.(),
    },
    {
      id: 'whoami',
      label: 'whoami',
      hint: 'identify yourself',
      glyph: '?',
      group: 'SYS',
      keywords: 'who user identity',
      run: () => toast('guest@jdenandro:~$ curiosity detected. respect.'),
    },
    {
      id: 'konami',
      label: '↑ ↑ ↓ ↓ ← → ← → B A',
      hint: 'you know what this is',
      glyph: '⌁',
      group: 'SYS',
      keywords: 'konami cheat code secret',
      run: () => toast('NO SHORTCUTS. type it with your hands, anywhere.'),
    },
  ];

  const programUrls = new Set(PROGRAMS.filter((p) => p.url).map((p) => p.url));
  const seen = new Set();
  const ENTRIES = [
    ...PROGRAMS,
    ...routes
      .filter((r) => {
        if (!r || !r.url || !r.label) return false;
        const key = r.url.replace(/\/$/, '') || '/';
        if (seen.has(key) || programUrls.has(r.url)) return false;
        seen.add(key);
        return true;
      })
      .map((r, i) => ({
        id: `go:${r.url}`,
        label: r.label,
        hint: r.hint || 'route',
        glyph: '→',
        group: 'GO',
        keywords: '',
        url: r.url,
      })),
  ];

  const routeCount = ENTRIES.filter((e) => e.group === 'GO').length + PROGRAMS.filter((p) => p.url).length;
  const programCount = PROGRAMS.length;

  /* ----- Recents ----- */
  const RECENT_KEY = 'dna_deck_recent';
  const getRecents = () => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch (error) {
      return [];
    }
  };
  const pushRecent = (id) => {
    try {
      const next = [id, ...getRecents().filter((r) => r !== id)].slice(0, 4);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch (error) {
      /* storage unavailable — recents are a nicety */
    }
  };

  /* ----- Fuzzy scoring: subsequence with word-start + streak bonuses ----- */
  const fuzzy = (query, text) => {
    const q = query.toLowerCase();
    const t = text.toLowerCase();
    let score = 0;
    let ti = 0;
    let streak = 0;
    const hits = [];
    for (let qi = 0; qi < q.length; qi++) {
      const ch = q[qi];
      if (ch === ' ') continue;
      let found = -1;
      while (ti < t.length) {
        if (t[ti] === ch) {
          found = ti;
          break;
        }
        ti++;
      }
      if (found === -1) return null;
      streak = found > 0 && hits.length && hits[hits.length - 1] === found - 1 ? streak + 1 : 0;
      score += 3 + streak * 3;
      if (found === 0 || /[\s\-_/.]/.test(t[found - 1])) score += 6;
      hits.push(found);
      ti = found + 1;
    }
    score -= (hits[hits.length - 1] - hits[0] - hits.length + 1) * 0.4; // gaps
    return { score, hits };
  };

  const match = (query, entry) => {
    const onLabel = fuzzy(query, entry.label);
    if (onLabel) return { score: onLabel.score + 4, hits: onLabel.hits };
    const onMeta = fuzzy(query, `${entry.hint} ${entry.keywords}`);
    if (onMeta) return { score: onMeta.score * 0.6, hits: [] };
    return null;
  };

  /* ----- Rendering ----- */
  let visible = [];
  let active = 0;

  const esc = (s) =>
    s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const highlight = (label, hits) => {
    if (!hits || !hits.length) return esc(label);
    let out = '';
    const set = new Set(hits);
    for (let i = 0; i < label.length; i++) {
      out += set.has(i) ? `<mark>${esc(label[i])}</mark>` : esc(label[i]);
    }
    return out;
  };

  const itemHtml = (entry, index, hits) => `
    <li
      class="deck__item"
      id="deck-item-${index}"
      role="option"
      aria-selected="${index === active}"
      data-deck-item="${index}"
    >
      <span class="deck__item-glyph" aria-hidden="true">${entry.glyph}</span>
      <span class="deck__item-body">
        <span class="deck__item-label">${highlight(entry.label, hits)}</span>
        <span class="deck__item-hint">${esc(entry.hint)}</span>
      </span>
      <span class="deck__item-meta" aria-hidden="true">${index === active ? '↵' : entry.group}</span>
    </li>`;

  const groupHtml = (title) => `<li class="deck__group" role="presentation" aria-hidden="true">${title}</li>`;

  const render = () => {
    const query = input.value.trim();
    let html = '';
    visible = [];

    if (!query) {
      const recents = getRecents()
        .map((id) => ENTRIES.find((e) => e.id === id))
        .filter(Boolean);
      const sections = [
        ['RECENT', recents],
        ['GO', ENTRIES.filter((e) => e.group === 'GO' && !recents.includes(e))],
        ['RUN', ENTRIES.filter((e) => e.group === 'RUN' && !recents.includes(e))],
        ['SYS', ENTRIES.filter((e) => e.group === 'SYS' && !recents.includes(e))],
      ];
      sections.forEach(([title, entries]) => {
        if (!entries.length) return;
        html += groupHtml(title);
        entries.forEach((entry) => {
          html += itemHtml(entry, visible.length, null);
          visible.push({ entry, hits: null });
        });
      });
    } else {
      const ranked = ENTRIES.map((entry) => ({ entry, m: match(query, entry) }))
        .filter((r) => r.m)
        .sort((a, b) => b.m.score - a.m.score)
        .slice(0, 24);
      ranked.forEach(({ entry, m }) => {
        html += itemHtml(entry, visible.length, m.hits);
        visible.push({ entry, hits: m.hits });
      });
    }

    list.innerHTML = html;
    empty.hidden = visible.length > 0;
    count.textContent = query ? `${visible.length} HIT${visible.length === 1 ? '' : 'S'}` : '';
    input.setAttribute('aria-activedescendant', visible.length ? `deck-item-${active}` : '');
  };

  const setActive = (index, scroll = true) => {
    if (!visible.length) return;
    active = (index + visible.length) % visible.length;
    list.querySelectorAll('[data-deck-item]').forEach((el) => {
      const isActive = Number(el.dataset.deckItem) === active;
      el.setAttribute('aria-selected', String(isActive));
      const meta = el.querySelector('.deck__item-meta');
      if (meta) meta.textContent = isActive ? '↵' : visible[Number(el.dataset.deckItem)].entry.group;
      if (isActive && scroll) el.scrollIntoView({ block: 'nearest' });
    });
    input.setAttribute('aria-activedescendant', `deck-item-${active}`);
  };

  /* ----- Execution ----- */
  const close = () => {
    if (typeof root.closeDialog === 'function') root.closeDialog();
    else dialog.close();
  };

  const execute = (entry) => {
    if (!entry) return;
    pushRecent(entry.id);
    close();
    if (entry.run) {
      // Synchronous: keeps the user-gesture activation alive so
      // window.open (instagram) isn't popup-blocked in Safari.
      entry.run();
      return;
    }
    if (entry.url) {
      const hash = entry.url.match(/^\/#(.+)$/);
      const onHome = location.pathname === '/' || location.pathname === '';
      if (hash && onHome) {
        const target = document.getElementById(hash[1]);
        if (target) {
          // Scroll only after the dialog has fully closed — closing
          // restores the scroll-locked body position and would stomp
          // an earlier smooth scroll.
          let scrolled = false;
          const go = () => {
            if (scrolled) return;
            scrolled = true;
            window.requestAnimationFrame(() =>
              target.scrollIntoView({ behavior: 'smooth', block: 'start' })
            );
          };
          dialog.addEventListener('close', go, { once: true });
          window.setTimeout(go, 700); // fallback if 'close' already fired
          return;
        }
      }
      window.setTimeout(() => {
        location.href = entry.url;
      }, 60);
    }
  };

  /* ----- Events ----- */
  input.addEventListener('input', () => {
    active = 0;
    render();
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(active + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(active - 1);
    } else if (event.key === 'Home' && !input.value) {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'Tab' && visible[active]) {
      event.preventDefault();
      input.value = visible[active].entry.label;
      render();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const typed = input.value.trim().toLowerCase();
      const exact = ENTRIES.find((e) => e.label.toLowerCase() === typed);
      execute(exact || visible[active]?.entry);
    }
  });

  list.addEventListener('click', (event) => {
    const item = event.target.closest('[data-deck-item]');
    if (item) execute(visible[Number(item.dataset.deckItem)]?.entry);
  });

  list.addEventListener('pointermove', (event) => {
    const item = event.target.closest('[data-deck-item]');
    if (item && Number(item.dataset.deckItem) !== active) setActive(Number(item.dataset.deckItem), false);
  });

  empty.querySelectorAll('[data-deck-try]').forEach((btn) => {
    btn.addEventListener('click', () => {
      input.value = btn.textContent.trim();
      active = 0;
      render();
      input.focus();
    });
  });

  /* ----- Open/close plumbing ----- */
  let booted = false;
  const bootStatus = () => {
    if (booted) {
      status.textContent = `SYS.NOMINAL · ${routeCount} ROUTES · ${programCount} PROGRAMS`;
      return;
    }
    booted = true;
    const line = `INDEX LOADED · ${routeCount} ROUTES · ${programCount} PROGRAMS`;
    let i = 0;
    const type = () => {
      status.textContent = line.slice(0, i) + (i < line.length ? '▌' : '');
      if (i++ <= line.length) window.setTimeout(type, 14);
    };
    type();
  };

  const onOpen = (prefill = '') => {
    input.value = prefill;
    active = 0;
    render();
    bootStatus();
    window.requestAnimationFrame(() => {
      input.focus();
      input.select();
    });
  };

  new MutationObserver(() => {
    if (dialog.hasAttribute('open')) onOpen(input.dataset.prefill || '');
    delete input.dataset.prefill;
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

  const open = (prefill = '') => {
    if (dialog.hasAttribute('open')) return;
    input.dataset.prefill = prefill;
    if (typeof root.showDialog === 'function') root.showDialog();
    else dialog.showModal();
  };

  document.addEventListener('keydown', (event) => {
    const editing =
      event.target instanceof HTMLElement &&
      (event.target.isContentEditable || /^(input|textarea|select)$/i.test(event.target.tagName));
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      dialog.hasAttribute('open') ? close() : open();
    } else if (event.key === '/' && !editing && !event.metaKey && !event.ctrlKey && !event.altKey) {
      if (!dialog.hasAttribute('open')) {
        event.preventDefault();
        open();
      }
    }
  });

  window.CommandDeck = { open, close, entries: ENTRIES };
})();
